import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';
import { detectBrowserTimezone } from '../utils/timezones';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (token && userData) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data.user);
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password, language = 'fr') => {
    try {
      const timezone = detectBrowserTimezone();
      
      const response = await authAPI.login({ email, password, language, timezone });
      const { token, refresh_token, user: userData, redirect, message } = response.data;
      
      // Store token and user data
      localStorage.setItem('token', token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true, redirect, message };
    } catch (error) {
      const message = error.response?.data?.error || 'Erreur de connexion';
      const errorCode = error.response?.data?.errorCode;
      return { success: false, error: message, errorCode };
    }
  };

  const signup = async ({
    email,
    password,
    name,
    language = 'en',
    timezone,
    companyName,
    industry
  }) => {
    try {
      const detectedTimezone = timezone || detectBrowserTimezone();
      const response = await authAPI.signup({
        email,
        password,
        name,
        language,
        timezone: detectedTimezone,
        companyName,
        industry
      });
      return { success: true, message: response.data.message, warning: response.data.warning };
    } catch (error) {
      // More detailed error handling
      const message = error.response?.data?.error || 
                     error.response?.data?.message || 
                     'Erreur lors de la création du compte';
      const errorCode = error.response?.data?.errorCode;
      return { success: false, error: message, errorCode };
    }
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await authAPI.logout({ refresh_token: refreshToken });
      } catch (error) {
        // Ignore logout errors
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const verifyEmail = async (token) => {
    try {
      const response = await authAPI.verifyEmail(token);
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Erreur de vérification';
      return { success: false, error: message };
    }
  };

  const resendVerification = async (email, language = 'en') => {
    try {
      const response = await authAPI.resendVerification(email, language);
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.error || 'Erreur lors de l\'envoi de l\'email';
      return { success: false, error: message };
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      const updatedUser = response.data.user;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    verifyEmail,
    resendVerification,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
